<template>
  <div
    id="search"
    @keydown.esc.exact.prevent="closeModal"
  >
    <div
      ref="modalContainer"
      class="search-container"
    >
      <div
        ref="firstFocusDiv"
        tabindex="0"
      />
      <button
        ref="closeButton"
        class="close-button"
        :aria-label="$t('search.closeSearchModal')"
        @click="closeModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="transparent"
          aria-hidden="true"
          role="img"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.21967 6.21967C6.51256 5.92678 6.98744 5.92678 7.28033 6.21967L17.7803 16.7197C18.0732 17.0126 18.0732 17.4874 17.7803 17.7803C17.4874 18.0732 17.0126 18.0732 16.7197 17.7803L6.21967 7.28033C5.92678 6.98744 5.92678 6.51256 6.21967 6.21967Z"
            fill="#FFFFFF"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M17.7803 6.21967C18.0732 6.51256 18.0732 6.98744 17.7803 7.28033L7.28033 17.7803C6.98744 18.0732 6.51256 18.0732 6.21967 17.7803C5.92678 17.4874 5.92678 17.0126 6.21967 16.7197L16.7197 6.21967C17.0126 5.92678 17.4874 5.92678 17.7803 6.21967Z"
            fill="#FFFFFF"
          />
        </svg>
        <span class="close-text">{{ $t('search.closeSearch') }}</span>
      </button>
      <SearchBar />
      <SearchResults />
    </div>
  </div>
</template>

<script setup>
import { useTemplateRef, onMounted, onUnmounted } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import SearchBar from './SearchBar.vue';
import SearchResults from './SearchResults.vue';
import { clientWorkWindowEvent } from '../../helpers/event-dispatch-helpers';

const route = useRoute();
const modalContainer = useTemplateRef('modalContainer');
const { deactivate } = useFocusTrap(modalContainer, { immediate: true });
function closeModal() {
  const currentQuery = { ...route.query };
  delete currentQuery.openSearch;
  deactivate();
  return navigateTo({
    path: route.path,
    query: currentQuery,
  });
}
onClickOutside(modalContainer, () => closeModal());

const closeButton = ref();
const hasFirstFocus = ref(true);
const firstFocusDiv = ref();
function handleFirstTab(event) {
  if (event.key === 'Tab' && hasFirstFocus.value === true) {
    hasFirstFocus.value = false;
    firstFocusDiv.value.tabIndex = -1;
    event.preventDefault();
    closeButton.value.focus();
    document.removeEventListener('keydown', handleFirstTab);
  }
}
onMounted(() => {
  clientWorkWindowEvent('search-modal-open');
  document.addEventListener('keydown', handleFirstTab);
});
onUnmounted(() => {
  clientWorkWindowEvent('search-modal-close');
  document.removeEventListener('keydown', handleFirstTab);
});
</script>

<style lang="scss" scoped>
@import '../global/scss/mixins/_breakpoints.scss';

#search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: rgba(34, 34, 34, 0.8);
  z-index: 10;

  @include screen('<=md') {
  }
  .search-container {
    width: 48vw;
    height: 64vh;
    background: transparent;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 16px;
  }
}
.close-button {
  opacity: 1;
  margin-left: auto;
  display: flex;
  align-items: center;
  background: none;
  padding: 5px;
  border: none;
  .close-text {
    color: var(--primary-white);
    font-size: 14px;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: 0.6px;
  }
}
.close-button:focus-visible {
  outline-offset: 2px;
}
@include screen('<=md') {
  #search {
    --search-bar-height: 74px;
    width: 100%;
    .search-container {
      position: absolute;
      top: 0;
      padding: 16px 24px;
      width: 90vw;
      height: calc(100vh - 48px);
      .search-bar {
        height: var(--search-bar-height);
      }
      .search-results {
        height: calc(100vh - var(--search-bar-height));
      }
    }
  }
}
@include screen('xs') {
  #search .search-container {
    --search-bar-height: 74px;
    width: 100%;
    height: 100%;
    gap: 0;
    padding: 0;
    .search-bar {
      height: var(--search-bar-height);
      border-radius: 0;
    }
    .search-results {
      border-radius: 0;
      height: calc(100vh - var(--search-bar-height));
    }
  }
}
</style>
